import max from 'lodash-es/max';
import min from 'lodash-es/min';

import type { ProductSearchCriteria } from '@jane/bloom/viewModels/productSearch';
import { SortOptions } from '@jane/bloom/viewModels/sortOptions';

type FiltersToTrack = Pick<
  ProductSearchCriteria,
  | 'activities'
  | 'brands'
  | 'categories'
  | 'feelings'
  | 'lineages'
  | 'prices'
  | 'productBrandSpecialIds'
  | 'ratings'
  | 'specialIds'
  | 'terpenes'
  | 'weights'
>;
/**
 * Mapping from ProductSearchCriteria keys to Algolia attribute names.
 */
const FILTERS_TO_TRACK: Record<keyof FiltersToTrack, string> = {
  activities: 'activities',
  brands: 'brand',
  categories: 'root_types',
  feelings: 'feelings',
  lineages: 'category',
  prices: 'bucket_price',
  productBrandSpecialIds: 'applicable_brand_special_ids',
  ratings: 'aggregate_rating',
  specialIds: 'applicable_special_ids',
  terpenes: 'compound_names',
  weights: 'available_weights',
};

/**
 * Handles range filters:
 * - cbdPotenciesMin
 * - cbdPotenciesMax
 * - thcPotenciesMin
 * - thcPotenciesMax
 */
const formatRangeFilters = (
  searchCriteria: ProductSearchCriteria
): string[] => {
  const { cbdPotenciesMax, cbdPotenciesMin, thcPotenciesMax, thcPotenciesMin } =
    searchCriteria;

  const rangeFilters: string[] = [];

  if (cbdPotenciesMin !== undefined || cbdPotenciesMax !== undefined) {
    const minRange =
      cbdPotenciesMin !== undefined ? `min_${cbdPotenciesMin}` : undefined;
    const maxRange =
      cbdPotenciesMax !== undefined ? `max_${cbdPotenciesMax}` : undefined;
    if (minRange || maxRange) {
      rangeFilters.push(
        ['percent_cbd', minRange, maxRange].filter(Boolean).join(':')
      );
    }
  }

  if (thcPotenciesMin !== undefined || thcPotenciesMax !== undefined) {
    const minRange =
      thcPotenciesMin !== undefined ? `min_${thcPotenciesMin}` : undefined;
    const maxRange =
      thcPotenciesMax !== undefined ? `max_${thcPotenciesMax}` : undefined;
    if (minRange || maxRange) {
      rangeFilters.push(
        ['percent_thc', minRange, maxRange].filter(Boolean).join(':')
      );
    }
  }

  return rangeFilters;
};

const getPriceRange = (prices?: string[]): string | undefined => {
  if (!prices || prices.length === 0) return undefined;

  const priceRanges = prices
    .map((price) => {
      const range = price.split(':');
      if (range[0] === '') {
        range[0] = '0';
      }
      if (range[1] === '') {
        range[1] = 'Infinity';
      }
      return range;
    })
    .flat()
    .map(parseFloat);

  return [min(priceRanges), max(priceRanges)].join('-');
};

const getRatingMin = (ratings?: string[]): number | undefined => {
  if (!ratings || ratings.length === 0) return undefined;

  const parsedRatings = ratings.map((rating) => parseInt(rating.split(':')[0]));

  return min(parsedRatings);
};

const formatRemainingFilters = (
  searchCriteria: ProductSearchCriteria
): string[] => {
  const filters: string[] = [];

  for (const k of Object.keys(FILTERS_TO_TRACK)) {
    const key = k as keyof FiltersToTrack;
    const values = searchCriteria[key];
    const algoliaAttribute = FILTERS_TO_TRACK[key];

    if (values && Array.isArray(values) && values.length > 0) {
      filters.push(...values.map((value) => `${algoliaAttribute}:${value}`));
    } else if (values && !Array.isArray(values)) {
      filters.push(`${algoliaAttribute}:${values}`);
    }
  }

  return filters;
};

/**
 * Mimics behavior in shared-ecomm/tracking/../useTrackSearch
 */
export const buildSearchedMenuProductsProperties = (
  searchCriteria: ProductSearchCriteria,
  hits: number
) => {
  const priceRange = getPriceRange(searchCriteria.prices);
  const aggregateRatingMin = getRatingMin(searchCriteria.ratings);
  const filters = [
    ...formatRangeFilters(searchCriteria),
    ...formatRemainingFilters(searchCriteria),
  ];

  return {
    aggregateRatingMin,
    filterCount: filters.length,
    filters,
    hits,
    priceRange,
    query: searchCriteria.searchText || '',
    sort: searchCriteria.sort || SortOptions.recommended,
  };
};
